// Main Gantt Chart Component for PlexiSystem 4.0
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, ChevronLeft, ChevronRight, Download, Filter, 
  Grid3x3, Layers, Plus, RefreshCw, Search, Settings,
  Users, ZoomIn, ZoomOut, Maximize2, Target, Clock,
  AlertCircle, CheckCircle, Activity, BarChart3,
  FileText, Link2, Sparkles, Save, Upload, Lock, UserCheck
} from 'lucide-react';
import { GanttTimeline } from './components/GanttTimeline';
import { GanttSidebar } from './components/GanttSidebar';
import { GanttToolbar } from './components/GanttToolbar';
import { GanttResourceView } from './components/GanttResourceView';
import { TaskDetailsModal } from './components/TaskDetailsModal';
import { GanttStats } from './components/GanttStats';
import { useGanttData } from './hooks/useGanttData';
import { useGanttActions } from './hooks/useGanttActions';
import { useGanttConfig } from './hooks/useGanttConfig';
import { GanttTask, GanttProject, ViewMode, GanttFilters } from './types/gantt.types';
import { exportGanttToPDF, exportGanttToExcel } from './utils/ganttExport';
import { calculateCriticalPath } from './utils/ganttCalculations';
import { useToast } from '../../hooks/useToast';

export const GanttChart: React.FC = () => {
  const { showToast } = useToast();
  const containerRef = useRef<HTMLDivElement>(null);
  
  // State
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedTask, setSelectedTask] = useState<GanttTask | null>(null);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [showResourceView, setShowResourceView] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<GanttFilters>({
    show_completed: true,
    show_cancelled: false
  });
  const [zoom, setZoom] = useState(100);
  const [showStats, setShowStats] = useState(true);
  const [autoSave, setAutoSave] = useState(true);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Hooks - Fetch data with real-time sync
  const {
    projects,
    tasks,
    dependencies,
    resources,
    users,
    machines,
    activeUsers,
    loading,
    error,
    lastUpdate,
    selectedProject,
    setSelectedProject,
    refetch
  } = useGanttData(filters);

  const { config, updateConfig } = useGanttConfig();
  
  // Actions with multi-user locking
  const {
    createProject,
    updateProject,
    deleteProject,
    createTask,
    updateTask,
    deleteTask,
    createDependency,
    deleteDependency,
    updateTaskProgress,
    moveTask,
    resizeTask,
    assignResource,
    removeResource,
    lockTaskForEdit,
    unlockTask,
    isLocking
  } = useGanttActions(refetch);

  // Calculate critical path
  useEffect(() => {
    if (tasks && config.showCriticalPath) {
      const criticalTasks = calculateCriticalPath(tasks);
      // Update tasks with critical path flag
      const updatedTasks = tasks.map(task => ({
        ...task,
        is_critical_path: criticalTasks.includes(task.id)
      }));
      // Update state with critical path info
    }
  }, [tasks, config.showCriticalPath]);

  // Handlers
  const handleTaskClick = async (task: GanttTask) => {
    // Try to lock task for editing
    const locked = await lockTaskForEdit(task.id);
    if (locked) {
      setSelectedTask(task);
      setShowTaskDetails(true);
    } else {
      const lockedBy = activeUsers.find(u => u.taskId === task.id);
      showToast(
        `Zadanie jest edytowane przez ${lockedBy?.user || 'innego użytkownika'}`, 
        'warning'
      );
    }
  };

  const handleTaskDrag = async (taskId: string, newStartDate: Date, newEndDate: Date) => {
    try {
      await moveTask(taskId, newStartDate, newEndDate);
      setUnsavedChanges(true);
    } catch (error) {
      console.error('Error moving task:', error);
    }
  };

  const handleTaskResize = async (taskId: string, newEndDate: Date) => {
    try {
      await resizeTask(taskId, newEndDate);
      setUnsavedChanges(true);
    } catch (error) {
      console.error('Error resizing task:', error);
    }
  };

  const handleProgressUpdate = async (taskId: string, progress: number) => {
    try {
      await updateTaskProgress(taskId, progress);
      setUnsavedChanges(true);
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleCreateTask = async () => {
    const newTask = {
      name: 'Nowe zadanie',
      project_id: selectedProject || projects[0]?.id || '',
      start_date: new Date(),
      end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // +7 days
      progress: 0,
      status: 'planned' as const,
      priority: 'medium' as const,
      assigned_to: [],
      assigned_names: []
    };

    try {
      await createTask(newTask);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleSave = async () => {
    try {
      // All changes are already saved to Supabase in real-time
      showToast('Wszystkie zmiany są zapisywane automatycznie', 'info');
      setUnsavedChanges(false);
    } catch (error) {
      showToast('Błąd przy zapisywaniu', 'error');
    }
  };

  const handleExportPDF = async () => {
    try {
      const exportData = { projects, tasks, resources, users, machines };
      await exportGanttToPDF(exportData, config, filters);
      showToast('PDF wyeksportowany', 'success');
    } catch (error) {
      showToast('Błąd przy eksporcie PDF', 'error');
    }
  };

  const handleExportExcel = async () => {
    try {
      const exportData = { projects, tasks, resources, users, machines };
      await exportGanttToExcel(exportData, config, filters);
      showToast('Excel wyeksportowany', 'success');
    } catch (error) {
      showToast('Błąd przy eksporcie Excel', 'error');
    }
  };

  // Close task modal and unlock
  const handleCloseTaskModal = useCallback(async () => {
    if (selectedTask) {
      await unlockTask(selectedTask.id);
    }
    setShowTaskDetails(false);
    setSelectedTask(null);
  }, [selectedTask, unlockTask]);

  // Loading state
  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Ładowanie wykresu Gantta z bazy danych...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && tasks.length === 0) {
    return (
      <div className="bg-red-900/20 border border-red-900 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-400 mb-4">Błąd ładowania danych: {error}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-red-900 text-white rounded-lg hover:bg-red-800 transition-colors"
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  // Calculate stats
  const stats = {
    totalTasks: tasks.length,
    completedTasks: tasks.filter(t => t.status === 'completed').length,
    delayedTasks: tasks.filter(t => t.status === 'delayed').length,
    criticalTasks: tasks.filter(t => t.is_critical_path).length,
    overallProgress: Math.round(
      tasks.reduce((acc, t) => acc + (t.progress || 0), 0) / tasks.length || 0
    ),
    activeUsers: activeUsers.length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-xl border-b border-white/10 sticky top-0 z-30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  Wykres Gantta
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                </h1>
                <p className="text-sm text-gray-400">
                  Zarządzanie harmonogramem projektów • Synchronizacja w czasie rzeczywistym
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Active users indicator */}
              {activeUsers.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 rounded-lg border border-blue-500/30">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-blue-400">
                    {activeUsers.length} aktywnych użytkowników
                  </span>
                </div>
              )}

              {/* Last update time */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg">
                <Clock className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-400">
                  Ostatnia aktualizacja: {new Date(lastUpdate).toLocaleTimeString('pl-PL')}
                </span>
              </div>

              {/* Auto-save indicator */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg">
                <CheckCircle className="w-3 h-3 text-green-400" />
                <span className="text-xs text-gray-400">Synchronizacja automatyczna</span>
              </div>

              {/* Refresh button */}
              <button
                onClick={refetch}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                disabled={loading}
                title="Odśwież dane"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Active users list */}
          {activeUsers.length > 0 && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-gray-500">Aktualnie edytują:</span>
              {activeUsers.map((au, idx) => (
                <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded">
                  <Lock className="w-3 h-3 text-yellow-400" />
                  <span className="text-gray-400">{au.user}:</span>
                  <span className="text-white">{au.taskName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <GanttToolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        zoom={zoom}
        onZoomChange={setZoom}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        showResourceView={showResourceView}
        onToggleResourceView={() => setShowResourceView(!showResourceView)}
        showStats={showStats}
        onToggleStats={() => setShowStats(!showStats)}
        onExportPDF={handleExportPDF}
        onExportExcel={handleExportExcel}
        onCreateTask={handleCreateTask}
        config={config}
        onConfigChange={updateConfig}
      />

      {/* Stats Cards */}
      {showStats && (
        <GanttStats stats={stats} data={{ projects, tasks, resources, users, machines }} />
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar with task list */}
        <GanttSidebar
          tasks={tasks}
          projects={projects}
          selectedProject={selectedProject}
          onProjectSelect={setSelectedProject}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onTaskClick={handleTaskClick}
          onTaskCreate={handleCreateTask}
          filters={filters}
          onFiltersChange={setFilters}
          showFilters={showFilters}
          activeUsers={activeUsers}
        />

        {/* Timeline View or Resource View */}
        <div className="flex-1 overflow-auto" ref={containerRef}>
          {showResourceView ? (
            <GanttResourceView
              tasks={tasks}
              resources={resources}
              users={users}
              machines={machines}
              viewMode={viewMode}
              zoom={zoom}
            />
          ) : (
            <GanttTimeline
              tasks={tasks}
              dependencies={dependencies}
              projects={projects}
              viewMode={viewMode}
              zoom={zoom}
              config={config}
              onTaskClick={handleTaskClick}
              onTaskDrag={handleTaskDrag}
              onTaskResize={handleTaskResize}
              onProgressUpdate={handleProgressUpdate}
              onDependencyCreate={createDependency}
              onDependencyDelete={deleteDependency}
              activeUsers={activeUsers}
            />
          )}
        </div>
      </div>

      {/* Task Details Modal */}
      <AnimatePresence>
        {showTaskDetails && selectedTask && (
          <TaskDetailsModal
            task={selectedTask}
            users={users}
            isLocked={true}
            onClose={handleCloseTaskModal}
            onUpdate={async (updatedTask) => {
              await updateTask(updatedTask.id, updatedTask);
              setUnsavedChanges(true);
              handleCloseTaskModal();
            }}
            onDelete={async () => {
              if (selectedTask) {
                await deleteTask(selectedTask.id);
                handleCloseTaskModal();
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Loading overlay when locking task */}
      {isLocking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-zinc-900 rounded-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-orange-500"></div>
            <span className="text-white">Blokowanie zadania do edycji...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttChart;